import { useState, useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';

import { type RowSelectionState } from '@tanstack/react-table';

import { BulkEditPermissionDialog, type BulkEditPermissionDialogProps } from './BulkEditPermissionDialog';
import { DeleteRecordingDialog } from './DeleteRecordingDialog';
import { EditRecordingDialog, type EditRecordingDialogProps } from './EditRecordingDialog';
import { RecordingLinksDialog } from './RecordingLinksDialog';
import { ViewAnalyticsDialog } from './ViewAnalyticsDialog';
import { type ClassRecordingExtended, type RecordingDialogProps } from '../classroom/ui-types';

type UseRecordingDialogsArgs = {
    recordings: ClassRecordingExtended[];
    setRecordings: (recordings: ClassRecordingExtended[]) => void;
    classUuid: string;
    userUuid: string;
    isInstructor: boolean;
    rowSelection: Record<string, boolean>;
    setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
};

export const useRecordingDialogs = ({
    recordings,
    setRecordings,
    classUuid,
    userUuid,
    rowSelection,
    setRowSelection
}: UseRecordingDialogsArgs) => {
    const initDialogProps = useMemo<RecordingDialogProps>(
        () => ({
            open: false,
            downloading: false,
            recording: undefined,
            recordings,
            setRecordings,
            classUuid,
            userUuid
        }),
        [recordings, setRecordings, classUuid, userUuid]
    );

    const [analyticsProps, setAnalyticsProps] = useState(initDialogProps);
    const [deleteProps, setDeleteProps] = useState(initDialogProps);
    const [editProps, setEditProps] = useState<EditRecordingDialogProps>(initDialogProps);
    const [linksProps, setLinksProps] = useState(initDialogProps);

    const [bulkPermissionProps, setBulkPermissionProps] = useState<BulkEditPermissionDialogProps>({
        ...initDialogProps,
        rowSelection: undefined,
        setRowSelection: undefined,
        open: false
    });

    const openAnalytics = useCallback(
        (recordingId: string) => {
            const rec = recordings.find((r) => r.recordingId === recordingId);
            if (!rec) {
                return;
            }
            setAnalyticsProps((current) => ({
                ...current,
                open: true,
                recording: rec
            }));
        },
        [recordings]
    );

    const openDelete = useCallback(
        (recordingId: string) => {
            const rec = recordings.find((r) => r.recordingId === recordingId);
            if (!rec) {
                return;
            }
            setDeleteProps({ ...initDialogProps, recording: rec, open: true });
        },
        [recordings, initDialogProps]
    );

    const openEdit = useCallback(
        (recordingId: string) => {
            const rec = recordings.find((r) => r.recordingId === recordingId);
            if (!rec) {
                return;
            }

            setRowSelection({});

            setEditProps({
                ...initDialogProps,
                recording: rec,
                open: true
            });
        },
        [recordings, initDialogProps, setRowSelection]
    );

    const openLinks = useCallback(
        (recordingId: string, downloading = false) => {
            const rec = recordings.find((r) => r.recordingId === recordingId);
            if (!rec) {
                return;
            }
            setLinksProps({ ...initDialogProps, recording: rec, downloading, open: true });
        },
        [recordings, initDialogProps]
    );

    const openBulkPermissions = useCallback(() => {
        setBulkPermissionProps({
            ...initDialogProps,
            rowSelection,
            setRowSelection,
            open: true
        });
    }, [initDialogProps, rowSelection, setRowSelection]);

    const generalOnClose = (cur) => ({ ...cur, open: false });

    const renderDialogs = () => (
        <>
            <ViewAnalyticsDialog {...analyticsProps} onClose={() => setAnalyticsProps(generalOnClose)} />
            <DeleteRecordingDialog
                {...deleteProps}
                onClose={() => setDeleteProps(generalOnClose)}
                recordings={recordings}
            />
            <EditRecordingDialog {...editProps} onClose={() => setEditProps(generalOnClose)} recordings={recordings} />
            <RecordingLinksDialog
                {...linksProps}
                onClose={() => setLinksProps(generalOnClose)}
                recordings={recordings}
            />
            <BulkEditPermissionDialog
                {...bulkPermissionProps}
                onClose={() => setBulkPermissionProps(generalOnClose)}
                recordings={recordings}
            />
        </>
    );

    return {
        renderDialogs,
        dialogMethods: {
            openAnalytics,
            openDelete,
            openEdit,
            openLinks,
            openBulkPermissions
        },
        dialogSetters: {
            setBulkPermissionProps,
            setLinksProps,
            setEditProps,
            setDeleteProps,
            setAnalyticsProps
        }
    };
};
